<template>
  <div class="kpis">
    <StatisticsKpi label="Factures émises" :value="integer(k.count)" hint="Hors factures annulées" />
    <StatisticsKpi label="CA facturé HT" :value="money(k.invoicedHt)" :hint="`Avoirs déduits : ${money(k.creditNotesHt)}`" />
    <StatisticsKpi label="Panier moyen HT" :value="money(k.averageBasketHt)" hint="Par facture" />
    <StatisticsKpi label="Panier médian HT" :value="money(k.medianBasketHt)" hint="La moitié des factures fait moins" />
    <StatisticsKpi label="Délai moyen de paiement" :value="days(k.averagePaymentDelay)" hint="Entre la facture et son règlement complet" />
    <StatisticsKpi label="Factures en retard" :value="money(k.overdueAmount)" :hint="`${integer(k.overdueCount)} facture(s) échue(s) à ce jour`" :tone="k.overdueCount ? 'bad' : ''" />
  </div>
  <div class="charts">
    <StatisticsCard :span="12" title="Chiffre d'affaires par période" :subtitle="`${revenueLabel(metric)} en barres, l'autre en pointillés`" :empty="!hasData(revenueValues)">
      <StatisticsChart :labels="labels" :datasets="revenueDatasets(data.series, metric)" />
    </StatisticsCard>
    <StatisticsCard :span="6" title="Saisonnalité" subtitle="CA facturé HT cumulé par mois de l'année : les mois les plus forts" :empty="!hasData(data.seasonality.map(row => row.invoicedHt))">
      <StatisticsChart :labels="data.seasonality.map(row => monthLabel(row.month))" :datasets="seasonalityDatasets" :legend="false" />
    </StatisticsCard>
    <StatisticsCard :span="6" title="Nombre de factures" subtitle="Factures émises par période" :empty="!hasData(data.series.map(row => row.count))">
      <StatisticsChart value-format="integer" :labels="labels" :datasets="[{label: 'Factures', data: data.series.map(row => row.count), color: PALETTE[1]}]" :legend="false" />
    </StatisticsCard>
    <StatisticsCard :span="6" title="Répartition par montant" subtitle="Factures par tranche de montant HT" :empty="!hasData(data.amountBands.map(row => row.count))">
      <StatisticsChart value-format="integer" :labels="data.amountBands.map(row => row.label)" :datasets="[{label: 'Factures', data: data.amountBands.map(row => row.count), color: PALETTE[3]}]" :legend="false" />
    </StatisticsCard>
    <StatisticsCard :span="6" title="Statuts des factures" subtitle="Part du TTC par statut" :empty="!data.statuses.length">
      <StatisticsChart type="doughnut" :labels="data.statuses.map(item => statusLabel(item.status))" :datasets="[{label: 'TTC', data: data.statuses.map(item => Math.max(0, item.ttc))}]" />
    </StatisticsCard>
  </div>
</template>

<script>
import StatisticsKpi from "@/statistics/components/StatisticsKpi.vue";
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import {StatisticsService} from "@/statistics/services/StatisticsService";
import {bucketLabel, days, hasData, integer, money, monthLabel} from "@/statistics/utils/format";
import {PALETTE} from "@/statistics/utils/chartTheme";
import {revenueDatasets, revenueLabel} from "@/statistics/utils/revenue";
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";

/**
 * Factures : chiffre d'affaires dans le temps, saisonnalité, paniers et délais de paiement.
 */
export default {
  name: 'InvoicesStats',
  components: {StatisticsKpi, StatisticsCard, StatisticsChart},
  props: {
    data: {type: Object, required: true},
  },
  data() {
    return {PALETTE};
  },
  computed: {
    k() {
      return this.data.kpis;
    },
    metric() {
      return StatisticsService.data.metric;
    },
    labels() {
      return this.data.series.map(row => bucketLabel(row.key));
    },
    revenueValues() {
      return this.data.series.flatMap(row => [row.invoicedHt, row.collectedHt]);
    },
    // Les trois mois les plus forts ressortent
    seasonalityDatasets() {
      const values = this.data.seasonality.map(row => row.invoicedHt);
      const strongest = [...values].sort((a, b) => b - a).slice(0, 3).filter(value => value > 0);
      return [{label: 'CA facturé HT', data: values, colors: values.map(value => (strongest.includes(value) ? PALETTE[0] : '#a9b6d6'))}];
    },
  },
  methods: {
    money, integer, days, hasData, monthLabel, revenueDatasets, revenueLabel,
    statusLabel: status => BUSINESS_STATUS_ENUM[status]?.label ?? status,
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/statistics/style/statistics.styl';
</style>
